import React, { FC, ReactElement, useContext } from "react";
import {
    Box,
    Link,
    Container,
    IconButton,
    Menu,
    MenuItem,
    Toolbar,
    Typography,
    Popper,
    Paper,
    MenuList,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AuthorizationState } from "../routes";
import { NavLink } from "react-router-dom";
import { UserContext } from "../App";
import { checkAuthentication } from "../UserStorage";

// Category interfaces
interface CategoryItem {
    key: string;
    title: string;
    path?: string;
    authType: AuthorizationState;
    children?: CategoryItem[];
}

interface NavCategory {
    key: string;
    title: string;
    items?: CategoryItem[];
    path?: string; // For categories without dropdowns (like Statistics)
    authType: AuthorizationState;
}

// Define categories
const categories: NavCategory[] = [
    {
        key: 'statistics',
        title: 'Statistics',
        path: '/',
        authType: AuthorizationState.Authorized
    },
    {
        key: 'promo',
        title: 'Promo',
        authType: AuthorizationState.Authorized,
        items: [
            { key: 'promo-matches', title: '1XBet promo', path: '/promo-matches', authType: AuthorizationState.Authorized },
            { key: 'promo-interstitial-settings', title: 'Interstitial ads', path: '/promo/interstitial-settings', authType: AuthorizationState.Authorized },
            { key: 'promo-chain-offers', title: 'Chain Offers', path: '/promo/chain-offers', authType: AuthorizationState.Authorized },
            { key: 'home-match-ads', title: 'Match Ads', path: '/home-match-ads', authType: AuthorizationState.Authorized },
            { key: 'video-placements', title: 'Videos', path: '/video-placements', authType: AuthorizationState.Authorized }
        ]
    },
    {
        key: 'games',
        title: 'Games',
        authType: AuthorizationState.Authorized,
        items: [
            { key: 'web-mini-games', title: 'Web Mini Games', path: '/web-games', authType: AuthorizationState.Authorized },
            {
                key: 'flashbet',
                title: 'FlashBet',
                authType: AuthorizationState.Authorized,
                children: [
                    { key: 'flashbet-stats', title: 'Statistics', path: '/flashbet/stats', authType: AuthorizationState.Authorized },
                    { key: 'flashbet-sports', title: 'Sports', path: '/flashbet/sports', authType: AuthorizationState.Authorized },
                    { key: 'flashbet-categories', title: 'Categories', path: '/flashbet/categories', authType: AuthorizationState.Authorized },
                    { key: 'flashbet-matches', title: 'Matches', path: '/flashbet/matches', authType: AuthorizationState.Authorized },
                    { key: 'flashbet-questions', title: 'Questions', path: '/flashbet/questions', authType: AuthorizationState.Authorized },
                    { key: 'flashbet-tasks', title: 'Tasks', path: '/flashbet/tasks', authType: AuthorizationState.Authorized }
                ]
            }
        ]
    },
    {
        key: 'marketing',
        title: 'Marketing',
        authType: AuthorizationState.Authorized,
        items: [
            { key: 'users', title: 'Users', path: '/users', authType: AuthorizationState.Authorized },
            { key: 'marketing-user-tags', title: 'User Tags', path: '/marketing/user-tags', authType: AuthorizationState.Authorized },
            { key: 'funnels', title: 'Funnels', path: '/funnels', authType: AuthorizationState.Authorized },
            { key: 'marketing-onboarding-funnel', title: 'Onboarding funnel', path: '/marketing/onboarding-funnel', authType: AuthorizationState.Authorized },
            { key: 'shop', title: 'Shop', path: '/shop', authType: AuthorizationState.Authorized },
            { key: 'marketing-subscriptions', title: 'Subscriptions', path: '/marketing/subscriptions', authType: AuthorizationState.Authorized },
            { key: 'marketing-subscription-offers', title: 'Subscription offers', path: '/marketing/subscriptions/offers', authType: AuthorizationState.Authorized },
            { key: 'marketing-tour', title: 'Tour rewards', path: '/marketing/tour-rewards', authType: AuthorizationState.Authorized },
            { key: 'marketing-one-click', title: 'Quick / one-click bet', path: '/marketing/one-click-bet-settings', authType: AuthorizationState.Authorized },
            { key: 'marketing-variety-bet-restrictions', title: 'Player Stats min stake', path: '/marketing/variety-bet-restrictions', authType: AuthorizationState.Authorized },
            { key: 'marketing-variety-version-gates', title: 'Variety version gates', path: '/marketing/variety-version-gates', authType: AuthorizationState.Authorized },
            { key: 'marketing-flash-bet', title: 'Flash Bet timing', path: '/marketing/flash-bet-settings', authType: AuthorizationState.Authorized },
            { key: 'marketing-app-update', title: 'App update', path: '/marketing/app-update-settings', authType: AuthorizationState.Authorized },
            { key: 'marketing-daily-quests', title: 'Daily quests', path: '/marketing/daily-quests', authType: AuthorizationState.Authorized },
            {
                key: 'betup-feed',
                title: 'BETUP Feed',
                authType: AuthorizationState.Authorized,
                children: [
                    { key: 'betup-feed-review', title: 'Review', path: '/marketing/betup-feed/review', authType: AuthorizationState.Authorized },
                    { key: 'betup-feed-posts', title: 'Posts', path: '/marketing/betup-feed/posts', authType: AuthorizationState.Authorized },
                    { key: 'betup-feed-reports', title: 'Reports', path: '/marketing/betup-feed/reports', authType: AuthorizationState.Authorized },
                    { key: 'betup-feed-top-players', title: 'Top players feed', path: '/marketing/betup-feed/top-players', authType: AuthorizationState.Authorized },
                    { key: 'betup-feed-channels', title: 'Channels', path: '/marketing/betup-feed/channels', authType: AuthorizationState.Authorized },
                    { key: 'betup-feed-publishing', title: 'Publishing', path: '/marketing/betup-feed/publishing', authType: AuthorizationState.Authorized }
                ]
            },
            { key: 'marketing-presentations', title: 'Presentations', path: '/marketing/presentations', authType: AuthorizationState.Authorized },
            { key: 'marketing-interstitials', title: 'Custom interstitials', path: '/marketing/interstitials', authType: AuthorizationState.Authorized },
            { key: 'marketing-rewarded-prompts', title: 'Rewarded video prompts', path: '/marketing/rewarded-prompts', authType: AuthorizationState.Authorized },
            { key: 'marketing-polls', title: 'Polls', path: '/marketing/polls', authType: AuthorizationState.Authorized },
            { key: 'marketing-push-notifications', title: 'Push notifications', path: '/marketing/push-notifications', authType: AuthorizationState.Authorized },
            { key: 'marketing-presentation-screen-registry', title: 'Presentation screens', path: '/marketing/presentations/screen-registry', authType: AuthorizationState.Authorized }
        ]
    },
    {
        key: 'betting',
        title: 'Betting',
        authType: AuthorizationState.Authorized,
        items: [
            { key: 'sports', title: 'Sports', path: '/sports', authType: AuthorizationState.Authorized },
            { key: 'virtual-sports', title: 'Virtual sports', path: '/virtual-sports', authType: AuthorizationState.Authorized },
            { key: 'league-catalog', title: 'League catalog', path: '/league-catalog', authType: AuthorizationState.Authorized },
            { key: 'competitions', title: 'Competitions', path: '/competitions', authType: AuthorizationState.Authorized },
            { key: 'battles', title: 'Battles', path: '/battles', authType: AuthorizationState.Authorized },
            { key: 'battle-stake-settings', title: 'Battle stake limits', path: '/battles/stake-settings', authType: AuthorizationState.Authorized },
            { key: 'private-clubs', title: 'Private club templates', path: '/private-clubs', authType: AuthorizationState.Authorized },
            { key: 'battle-agents', title: 'Battle agents', path: '/battle-agents', authType: AuthorizationState.Authorized },
            { key: 'battle-agents-bots', title: 'Battle agent bots', path: '/battle-agents/bots', authType: AuthorizationState.Authorized },
            { key: 'battle-agents-history', title: 'Battle agent history', path: '/battle-agents/history', authType: AuthorizationState.Authorized },
            { key: 'battle-agents-stats', title: 'Battle agent stats', path: '/battle-agents/stats', authType: AuthorizationState.Authorized },
            { key: 'quizzes', title: 'Quizzes', path: '/quizzes', authType: AuthorizationState.Authorized },
            { key: 'quiz-configurations', title: 'Quiz (sport & leagues)', path: '/quiz/configurations', authType: AuthorizationState.Authorized },
            {
                key: 'matches-menu',
                title: 'Matches',
                authType: AuthorizationState.Authorized,
                children: [
                    { key: 'matches', title: 'Matches', path: '/matches', authType: AuthorizationState.Authorized },
                    { key: 'matches-ai-predictions', title: 'AI predictions', path: '/matches/ai-predictions', authType: AuthorizationState.Authorized }
                ]
            }
        ]
    },
    {
        key: 'support',
        title: 'Support',
        path: '/support',
        authType: AuthorizationState.Authorized
    },
    {
        key: 'ai',
        title: 'AI',
        authType: AuthorizationState.Authorized,
        items: [
            { key: 'ai-overview', title: 'Overview', path: '/ai', authType: AuthorizationState.Authorized },
            { key: 'ai-tasks', title: 'Tasks', path: '/ai/tasks', authType: AuthorizationState.Authorized },
            { key: 'ai-agents', title: 'Agents', path: '/ai/agents', authType: AuthorizationState.Authorized },
            { key: 'ai-runs', title: 'Runs', path: '/ai/runs', authType: AuthorizationState.Authorized },
            { key: 'ai-conversations', title: 'Conversations', path: '/ai/conversations', authType: AuthorizationState.Authorized },
            { key: 'ai-tools', title: 'Tool calls', path: '/ai/tools', authType: AuthorizationState.Authorized },
            { key: 'ai-usage', title: 'Usage', path: '/ai/usage', authType: AuthorizationState.Authorized },
            { key: 'ai-workflows', title: 'Workflows', path: '/ai/workflows', authType: AuthorizationState.Authorized },
            { key: 'ai-budgets', title: 'Budgets', path: '/ai/budgets', authType: AuthorizationState.Authorized },
            { key: 'ai-models', title: 'Models & keys', path: '/ai/models', authType: AuthorizationState.Authorized },
            { key: 'ai-mcp-servers', title: 'MCP servers', path: '/ai/mcp-servers', authType: AuthorizationState.Authorized }
        ]
    },
    {
        key: 'health',
        title: 'Health',
        path: '/health',
        authType: AuthorizationState.Authorized
    }
];

const Navbar: FC = (): ReactElement => {
    const [state, setState] = React.useState({ 
        navMenuAnchor: null, 
        userMenuAnchor: null 
    });
    const [openDropdown, setOpenDropdown] = React.useState<string | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const handleOpenNavMenu = (event: any) => {
        setState({ ...state, navMenuAnchor: event.currentTarget });
    };

    const handleCloseNavMenu = () => {
        setState({ ...state, navMenuAnchor: null });
    };

    const handleCategoryHover = (event: React.MouseEvent<HTMLElement>, categoryKey: string) => {
        setAnchorEl(event.currentTarget);
        setOpenDropdown(categoryKey);
    };

    const handleCategoryLeave = () => {
        setOpenDropdown(null);
        setAnchorEl(null);
    };

    const handleDropdownClose = () => {
        setOpenDropdown(null);
        setAnchorEl(null);
    };

    let context = useContext(UserContext);

    const isItemVisible = (authType: AuthorizationState) => {
        if (authType === AuthorizationState.Any)
            return true;
        if (authType === AuthorizationState.Authorized && context.isAuthenticated)
            return true;

        if (authType === AuthorizationState.Unauthorized && !context.isAuthenticated)
            return true;

        return false;
    }

    const isCategoryItemVisible = (item: CategoryItem): boolean => {
        return isItemVisible(item.authType) && (!item.children || item.children.some(child => isCategoryItemVisible(child)));
    }

    const renderMobileItem = (item: CategoryItem, level = 1): React.ReactNode => {
        if (!isCategoryItemVisible(item)) return null;
        if (item.children) {
            return (
                <Box key={item.key}>
                    <MenuItem disabled sx={{ pl: 2 + level * 2 }}>
                        <Typography textAlign="center" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                            {item.title}
                        </Typography>
                    </MenuItem>
                    {item.children.map(child => renderMobileItem(child, level + 1))}
                </Box>
            );
        }
        if (!item.path) return null;
        return (
            <Link
                key={item.key}
                component={NavLink}
                to={item.path}
                color="black"
                underline="none"
                variant="button"
            >
                <MenuItem onClick={handleCloseNavMenu} sx={{ pl: 2 + level * 2 }}>
                    <Typography textAlign="center">{item.title}</Typography>
                </MenuItem>
            </Link>
        );
    }

    const renderDesktopItem = (item: CategoryItem, level = 0): React.ReactNode => {
        if (!isCategoryItemVisible(item)) return null;
        if (item.children) {
            return (
                <Box key={item.key}>
                    <MenuItem disabled sx={{ minWidth: 180, pl: 2 + level * 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                            {item.title}
                        </Typography>
                    </MenuItem>
                    {item.children.map(child => renderDesktopItem(child, level + 1))}
                </Box>
            );
        }
        if (!item.path) return null;
        return (
            <MenuItem 
                key={item.key}
                onClick={handleDropdownClose}
                sx={{ minWidth: 180, pl: 2 + level * 2 }}
            >
                <Link
                    component={NavLink}
                    to={item.path}
                    color="black"
                    underline="none"
                    sx={{ width: '100%', textDecoration: 'none' }}
                >
                    {item.title}
                </Link>
            </MenuItem>
        );
    }

    let isAuthenticated = checkAuthentication();
    return (
        <Box
            sx={{
                width: "100%",
                height: "auto",
                backgroundColor: "secondary.main",
            }}
        >
            <Container maxWidth="xl">
                <Toolbar disableGutters>
                    <Typography
                        variant="h6"
                        noWrap
                        sx={{
                            mr: 2,
                            display: { xs: "none", md: "flex" },
                        }}
                    >
                        {isAuthenticated ?
              "BETUP Dashboard" : "BETUP"}
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}>
                        <IconButton
                            size="large"
                            aria-label="account of current user"
                            aria-controls="menu-appbar"
                            aria-haspopup="true"
                            onClick={handleOpenNavMenu}
                            color="inherit"
                        >
                            <MenuIcon />
                        </IconButton>
                        <Menu
                            id="menu-appbar"
                            anchorEl={state.navMenuAnchor}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            keepMounted
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                            open={Boolean(state.navMenuAnchor)}
                            onClose={handleCloseNavMenu}
                            sx={{
                                display: { xs: "block", md: "none" },
                            }}
                        >
                            {categories.filter((category) => isItemVisible(category.authType)).map((category) => (
                                <Box key={category.key}>
                                    {category.path ? (
                                        // Category with direct link
                                        <Link
                                            component={NavLink}
                                            to={category.path}
                                            color="black"
                                            underline="none"
                                            variant="button"
                                        >
                                            <MenuItem onClick={handleCloseNavMenu}>
                                                <Typography textAlign="center">{category.title}</Typography>
                                            </MenuItem>
                                        </Link>
                                    ) : (
                                        // Category with subcategories
                                        <>
                                            <MenuItem disabled>
                                                <Typography 
                                                    textAlign="center" 
                                                    sx={{ fontWeight: 'bold', color: 'primary.main' }}
                                                >
                                                    {category.title}
                                                </Typography>
                                            </MenuItem>
                                            {category.items?.map((item) => renderMobileItem(item))}
                                        </>
                                    )}
                                </Box>
                            ))}
                        </Menu>
                    </Box>
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        sx={{ flexGrow: 1, display: { xs: "flex", md: "none" } }}
                    >
                        {isAuthenticated ?
              "BETUP Dashboard" : "BETUP"}
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                                marginLeft: "1rem",
                            }}
                        >
                            {categories.filter((category) => isItemVisible(category.authType)).map((category) => (
                                <Box
                                    key={category.key}
                                    sx={{ position: 'relative' }}
                                    onMouseEnter={(e) => category.items && handleCategoryHover(e, category.key)}
                                    onMouseLeave={category.items ? handleCategoryLeave : undefined}
                                >
                                    {category.path ? (
                                        // Category with direct link (no dropdown)
                                        <Link
                                            component={NavLink}
                                            to={category.path}
                                            color="black"
                                            underline="none"
                                            variant="button"
                                            sx={{ fontSize: "large", marginLeft: "2rem" }}
                                        >
                                            {category.title}
                                        </Link>
                                    ) : (
                                        // Category with dropdown
                                        <Typography
                                            variant="button"
                                            sx={{ 
                                                fontSize: "large", 
                                                marginLeft: "2rem",
                                                cursor: 'pointer',
                                                color: 'black',
                                                display: 'flex',
                                                alignItems: 'center',
                                                '&:hover': {
                                                    opacity: 0.7
                                                }
                                            }}
                                        >
                                            {category.title}
                                            <ExpandMoreIcon sx={{ ml: 0.5, fontSize: '1rem' }} />
                                        </Typography>
                                    )}
                                    
                                    {/* Dropdown Menu */}
                                    {category.items && (
                                        <Popper
                                            open={openDropdown === category.key}
                                            anchorEl={anchorEl}
                                            placement="bottom-start"
                                            sx={{ zIndex: 1300 }}
                                        >
                                            <Paper 
                                                elevation={3}
                                                onMouseEnter={() => setOpenDropdown(category.key)}
                                                onMouseLeave={handleCategoryLeave}
                                                sx={{ mt: 0.5 }}
                                            >
                                                <MenuList>
                                                    {category.items.map((item) => renderDesktopItem(item))}
                                                </MenuList>
                                            </Paper>
                                        </Popper>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                    <Box sx={{ flexGrow: 0, display: { xs: context.isAuthenticated ? "flex" : "none" }, alignItems: "center", justifyContent: "right" }}>
                        <Link
                            key="logout"
                            component={NavLink}
                            to="/logout"
                            color="black"
                            underline="none"
                            variant="button">Logout</Link>
                    </Box>
                </Toolbar>
            </Container>
        </Box>
    );
};

export default Navbar;
